import { decodeJwtPayload, claimsFromDecodedToken } from './auth-claims'
import { isPlatformRole } from './server/admin-sections'
import { toHasuraAuthRole } from './staff-role-collapse'

/**
 * Per-tab active-role override — the official Hasura/Nhost role-selection
 * mechanism: the JWT carries `x-hasura-allowed-roles`, and a request may run
 * as any of them by sending an `x-hasura-role` header. No token re-mint.
 *
 * The override is sessionStorage-backed (per tab, survives reloads, never
 * leaks across tabs — mirrors the impersonation overlay's tab scoping) and is
 * attached by the Apollo auth link ONLY when it passes the guards in
 * `roleHeaderForToken`: valid for the token about to be sent, and not the
 * token's own default role. Impersonation requests never carry it.
 */

const STORAGE_KEY = 'taskyinn-active-role'

/**
 * Cookie twin of the override. The cookie exists so the SERVER can see the
 * acted-as role and SSR the correct chrome (sidebar seed) — sessionStorage is
 * invisible to RSC, which made every acting-as page paint the real role's
 * menu first and swap after hydration (the "jerk", founder 2026-07-12).
 * Consequence: the override is browser-wide, not per-tab — a fresh tab adopts
 * it (sessionStorage mirrors the cookie for sync render reads). Impersonation
 * keeps its own separate per-tab overlay token; this does not affect it.
 */
export const ACTIVE_ROLE_COOKIE = 'taskyinn-active-role'

/** Fired on window whenever the override changes (same tab). */
export const ACTIVE_ROLE_EVENT = 'taskyinn:active-role-changed'

function readCookieOverride(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)taskyinn-active-role=([^;]+)/)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

export function getActiveRoleOverride(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const tab = window.sessionStorage.getItem(STORAGE_KEY)
    if (tab) return tab
    // Fresh tab: adopt the browser-wide override so renders and requests agree.
    const fromCookie = readCookieOverride()
    if (fromCookie) window.sessionStorage.setItem(STORAGE_KEY, fromCookie)
    return fromCookie
  } catch {
    return null
  }
}

export function setActiveRoleOverride(role: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (role) {
      window.sessionStorage.setItem(STORAGE_KEY, role)
      document.cookie = `${ACTIVE_ROLE_COOKIE}=${encodeURIComponent(role)}; path=/; max-age=43200; samesite=lax`
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY)
      document.cookie = `${ACTIVE_ROLE_COOKIE}=; path=/; max-age=0; samesite=lax`
    }
    window.dispatchEvent(new CustomEvent(ACTIVE_ROLE_EVENT, { detail: { role } }))
  } catch {
    // storage unavailable — override simply stays off
  }
}

// ── Automatic per-property role ──────────────────────────────────────────────
//
// A user can hold a different role at each property (property_members.role_key
// is per property) while the JWT carries ONE default role. When the flag is on,
// requests run as the ACTIVE property's role: SelectionContext resolves the
// role_key on bootstrap and on every tenant/property switch (before the WS
// reconnect) and publishes it here; the Apollo links attach it via
// roleHeaderForToken. Kill switch: unset NEXT_PUBLIC_PER_PROPERTY_ROLE and
// redeploy — everything falls back to the token's default role.

const PER_PROPERTY_ROLE_ENABLED = process.env.NEXT_PUBLIC_PER_PROPERTY_ROLE === '1'

let propertyRoleKey: string | null = null

/** Published by SelectionContext whenever the active property (or its role) changes. */
export function setPropertyRoleKey(roleKey: string | null): void {
  propertyRoleKey = roleKey
}

export function getPropertyRoleKey(): string | null {
  return propertyRoleKey
}

/**
 * Wave 6 — the Hasura role a DASHBOARD render should assume its requests run
 * as, as a PURE function of the same inputs `resolveRoleHeader`'s dashboard
 * branch reads: manual (non-platform) override wins, then the per-property
 * auto-pin (flag-gated), each mapped through the staff collapse and validated
 * against the token's allowed roles; otherwise the token's default role.
 * Used by the capability layer to pick the governance tier (owner/admin
 * bypass · tenant_admin role-governed · staff membership-governed) — the tier
 * must follow the role the DATA layer evaluates, or the UI and the database
 * would disagree about what is allowed.
 */
export function deriveActiveHasuraRole(opts: {
  defaultRole: string | null
  allowedRoles: readonly string[]
  overrideRole: string | null
  propertyRoleKey: string | null
}): string | null {
  const manual = isPlatformRole(opts.overrideRole) ? null : opts.overrideRole
  const matrixEffective =
    manual ?? (PER_PROPERTY_ROLE_ENABLED ? opts.propertyRoleKey : null)
  if (matrixEffective) {
    const effective = opts.allowedRoles.includes(matrixEffective)
      ? matrixEffective
      : toHasuraAuthRole(matrixEffective)
    if (opts.allowedRoles.includes(effective)) return effective
  }
  return opts.defaultRole
}

/**
 * The role Hasura ACTUALLY evaluated on the most recent request: whatever
 * `roleHeaderForToken` last returned, or the token's default role when it
 * returned null (no `x-hasura-role` header ⇒ Hasura uses the default).
 *
 * ⛔ This exists because error monitoring kept attributing permission failures
 * to the wrong role. `getPropertyRoleKey()` is NOT the answer: the header is
 * suppressed when the property role is absent from `x-hasura-allowed-roles` or
 * equals the default role, so the property role can differ from the role that
 * ran. On 2026-08-04 a batch of denials was read as "ran as tenant_admin" — a
 * role that demonstrably held every grant involved — which sent two separate
 * investigations down a false trail. Record what was SENT, not what was known.
 */
let lastRequestRole: string | null = null

export function getLastRequestRole(): string | null {
  return lastRequestRole
}

/**
 * The `x-hasura-role` header value for a request about to be sent with
 * `accessToken`, or null when no header should be attached.
 *
 * Precedence: manual per-tab override (role switcher) wins over the automatic
 * per-property role. Guards for either source:
 *  - it is in the token's `x-hasura-allowed-roles` (Hasura would reject it
 *    otherwise — never send a header we know is invalid),
 *  - it differs from the token's default role (no-op otherwise).
 */
/** Growth-portal roles, highest privilege first (auto-pin picks the first held). */
export const GROWTH_PORTAL_ROLES = [
  'growth_owner',
  'growth_regional_manager',
  'growth_sales_manager',
  'growth_cs',
  'growth_sales_rep',
] as const

export function roleHeaderForToken(accessToken: string | undefined | null): string | null {
  const header = resolveRoleHeader(accessToken)
  // Remember what Hasura will actually evaluate: the header when we send one,
  // otherwise the token's default role. Recorded HERE, at the single decision
  // point, so it cannot drift from what the transport does — every early
  // `return null` below is a "runs as the default role" case.
  lastRequestRole =
    header ??
    (accessToken ? claimsFromDecodedToken(decodeJwtPayload(accessToken))?.role ?? null : null)
  return header
}

function resolveRoleHeader(accessToken: string | undefined | null): string | null {
  // Platform-admin surfaces (`/admin`): the one browser-side admin query
  // (admin/tenants/TenantsClient) must run AS the switched platform role so a
  // support/billing_admin/onboarding preview sees that role's view. But ONLY a
  // platform role the token genuinely allows may be sent here — a TENANT role
  // would tenant-scope the cross-tenant admin queries (e2e 2026-07-12:
  // /admin/tenants showed 1 of 20 tenants), which is exactly the failure this
  // branch exists to prevent. No override, or a non-platform / unheld override,
  // yields null: the query then runs as the token's own (cross-tenant) role.
  if (typeof window !== 'undefined' && /^\/admin(\/|$)/.test(window.location.pathname)) {
    const adminClaims = accessToken ? claimsFromDecodedToken(decodeJwtPayload(accessToken)) : null
    if (!adminClaims) return null
    const override = getActiveRoleOverride()
    if (
      isPlatformRole(override) &&
      adminClaims.allowedRoles.includes(override) &&
      adminClaims.role !== override
    ) {
      return override
    }
    return null
  }
  const claims = accessToken ? claimsFromDecodedToken(decodeJwtPayload(accessToken)) : null

  // Growth portal: requests must run AS a growth role — growth tables grant
  // only growth_* selects, so an admin/staff holder browsing /growth with
  // their default role gets validation-failed on every query (prod
  // 2026-07-12). Landing there is default-role-gated (auth-redirect), but any
  // holder may enter; pin their strongest held growth role. A manual switch
  // to a growth role is respected; a non-growth override is ignored here.
  if (typeof window !== 'undefined' && /^\/growth(\/|$)/.test(window.location.pathname) && claims) {
    if ((GROWTH_PORTAL_ROLES as readonly string[]).includes(claims.role ?? '')) return null
    const manual = getActiveRoleOverride()
    const pinned =
      manual && (GROWTH_PORTAL_ROLES as readonly string[]).includes(manual)
        ? manual
        : GROWTH_PORTAL_ROLES.find(r => claims.allowedRoles.includes(r))
    return pinned ?? null
  }

  // A PLATFORM override belongs to `/admin` only. If one lingers in the cookie
  // (the switcher can set it from the hotel portal to jump into `/admin`, and it
  // survives a browser-back to `/dashboard`), it must be IGNORED here: platform
  // roles hold no grant on any hotel table, so sending one as `x-hasura-role`
  // would fail every dashboard query. Fall through to the per-property role.
  const manual = getActiveRoleOverride()
  const usableManual = isPlatformRole(manual) ? null : manual
  const matrixEffective = usableManual ?? (PER_PROPERTY_ROLE_ENABLED ? propertyRoleKey : null)
  if (!matrixEffective || !accessToken) return null
  if (!claims) return null
  // Wave 5c2: role_key values are MATRIX roles, not Hasura roles. The header
  // Hasura sees must be the AUTH role the matrix role maps to — ceilings pass
  // through, every operational/custom key maps to 'staff'. The publish path
  // (SelectionContext → setPropertyRoleKey) still carries the raw role_key
  // for UI/capability decisions; only this derivation changes.
  // A value the token genuinely allows (a real Hasura role — ceilings, portal
  // roles) passes through untouched; anything else is a matrix key and maps.
  const effective = claims.allowedRoles.includes(matrixEffective)
    ? matrixEffective
    : toHasuraAuthRole(matrixEffective)
  if (!claims.allowedRoles.includes(effective)) return null
  if (claims.role === effective) return null
  return effective
}
